import React, { useEffect, useState, useCallback } from "react";
import PropTypes from "prop-types";
import { Link, useNavigate } from "react-router-dom";
import { DatePicker, Spin, Alert } from "antd";
import dayjs from "dayjs";
import Table from "../../../../core/pagination/datatable.jsx";
import { ROLL_CALL_API_BASE_URL } from "../../RollCall/RollCallConfig/RollCallConfig";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import ImageWithBasePath from "../../../../core/img/imagewithbasebath";

function bandFor(score) {
  if (score === null || score === undefined) return "none";
  if (score > 80) return "green";
  if (score >= 50) return "orange";
  return "red";
}

const BAND_COLORS = {
  green: { text: "#047857", dot: "#10b981", pdf: [16, 185, 129] },
  orange: { text: "#b45309", dot: "#f59e0b", pdf: [245, 158, 11] },
  red: { text: "#be123c", dot: "#f43f5e", pdf: [244, 63, 94] },
  none: { text: "#94a3b8", dot: "#cbd5e1", pdf: [203, 213, 225] },
};

function ScoreCircle({ score }) {
  const colors = BAND_COLORS[bandFor(score)];
  return (
    <span
      className="d-inline-flex align-items-center justify-content-center fw-bold text-white"
      style={{
        backgroundColor: colors.dot,
        borderRadius: "50%",
        width: 40,
        height: 40,
        fontSize: 13,
      }}
    >
      {score === null || score === undefined ? "—" : score}
    </span>
  );
}

ScoreCircle.propTypes = {
  score: PropTypes.number,
};

export default function GrowthAnalysisReport() {
  const navigate = useNavigate();

  const [tokenData, setTokenData] = useState(null);
  const [superIdData, setSuperIdData] = useState(null);
  const [userData, setUserData] = useState(null);

  const [selectedMonth, setSelectedMonth] = useState(dayjs());
  const [monthKeys, setMonthKeys] = useState([]);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const token = sessionStorage.getItem("authToken");
    const superId = sessionStorage.getItem("superId");
    const storedUserData = sessionStorage.getItem("userData");

    if (token && superId) {
      setTokenData(token);
      setSuperIdData(superId);
      if (storedUserData) {
        try {
          setUserData(JSON.parse(storedUserData));
        } catch {
          setUserData(null);
        }
      }
    } else {
      navigate("/signin");
    }
  }, [navigate]);

  const fetchGrowthReport = useCallback(
    async (monthValue) => {
      if (!tokenData || !superIdData || !monthValue) return;

      setLoading(true);
      setError(null);
      try {
        const startDate = monthValue.startOf("month").format("YYYY-MM-DD");
        const endDate = monthValue.endOf("month").format("YYYY-MM-DD");

        const url = `${ROLL_CALL_API_BASE_URL}portal/kpi/GrowthAnalysisReport?SuperId=${superIdData}&StartDate=${startDate}&EndDate=${endDate}`;
        const res = await fetch(url, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${tokenData}`,
          },
        });

        if (!res.ok) {
          throw new Error(
            `GrowthAnalysisReport request failed (${res.status})`,
          );
        }

        const json = await res.json();
        if (!json.status) {
          setData([]);
          setMonthKeys([]);
          return;
        }

        const rows = json.ResultData || [];
        setData(rows);

        if (rows.length > 0) {
          const keys = Object.keys(rows[0]).filter(
            (k) => k !== "BranchId" && k !== "BranchName",
          );
          setMonthKeys(keys);
        } else {
          setMonthKeys([]);
        }
      } catch (err) {
        setError(err.message || "Failed to load growth analysis report");
        setData([]);
        setMonthKeys([]);
      } finally {
        setLoading(false);
      }
    },
    [tokenData, superIdData],
  );

  useEffect(() => {
    fetchGrowthReport(selectedMonth);
  }, [fetchGrowthReport, selectedMonth]);

  const handleMonthChange = (val) => {
    if (val) setSelectedMonth(val);
  };

  // const latestMonthKey = monthKeys[monthKeys.length - 1];
  // const prevMonthKey = monthKeys[monthKeys.length - 2];

  const columns = [
    {
      title: "Branch",
      dataIndex: "BranchName",
      key: "BranchName",
      sorter: (a, b) => a.BranchName.localeCompare(b.BranchName),
      render: (text) => ({
        children: <span className="fw-semibold">{text}</span>,
      }),
    },
    ...monthKeys.map((m) => ({
      title: m,
      dataIndex: m,
      key: m,
      align: "center",
      sorter: (a, b) => (a[m] ?? -1) - (b[m] ?? -1),
      render: (text, record) => ({
        children: <ScoreCircle score={record[m]} />,
      }),
    })),
    // {
    //   title: "Remarks",
    //   key: "Remarks",
    //   render: (text, record) => {
    //     const remark = buildRemark(
    //       record[latestMonthKey],
    //       record[prevMonthKey],
    //     );
    //     return (
    //       <span
    //         className="fst-italic small"
    //         style={{ color: REMARK_COLOR[remark.tone] }}
    //       >
    //         {remark.text}
    //       </span>
    //     );
    //   },
    // },
  ];

  const handleDownloadPdf = async () => {
    if (!data.length || !monthKeys.length) return;

    const doc = new jsPDF({ orientation: "portrait" });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const marginX = 14;

    const generatedByName =
      userData?.UserName || userData?.Name || userData?.BusinessName || "User";
    const generatedAt = dayjs().format("DD-MMM-YYYY hh:mm A");

    let cursorY = 16;

    // ---- Organization name, top and centered ----
    if (userData?.Organization) {
      doc.setFontSize(16);
      doc.setFont(undefined, "bold");
      doc.setTextColor("#0f172a");
      doc.text(userData.Organization, pageWidth / 2, cursorY, {
        align: "center",
      });
      cursorY += 7;
    }

    // ---- Report title, centered ----
    doc.setFontSize(11);
    doc.setFont(undefined, "bold");
    doc.setTextColor("#1e293b");
    doc.text("Consolidated Growth Analysis Report", pageWidth / 2, cursorY, {
      align: "center",
    });

    // ---- Generated By + Time, right-aligned, same line as the title — kept small/quiet ----
    doc.setFontSize(7);
    doc.setFont(undefined, "normal");
    doc.setTextColor("#94a3b8");
    doc.text(
      `Generated By: ${generatedByName}`,
      pageWidth - marginX,
      cursorY - 3,
      {
        align: "right",
      },
    );
    doc.text(generatedAt, pageWidth - marginX, cursorY + 3, { align: "right" });

    cursorY += 7;

    doc.setDrawColor(226, 232, 240);
    doc.line(marginX, cursorY, pageWidth - marginX, cursorY);
    cursorY += 6;

    const head = [["Branch", ...monthKeys]];
    const body = data.map((row) => [
      row.BranchName,
      ...monthKeys.map((m) =>
        row[m] === null || row[m] === undefined ? null : row[m],
      ),
    ]);

    autoTable(doc, {
      head,
      body,
      startY: cursorY,
      theme: "grid",
      margin: { bottom: 20 },
      styles: {
        fontSize: 8,
        halign: "center",
        valign: "middle",
        lineWidth: 0.2,
        lineColor: [148, 163, 184],
        minCellHeight: 10,
      },
      headStyles: {
        fillColor: [71, 85, 105],
        textColor: 255,
        lineWidth: 0.2,
        lineColor: [148, 163, 184],
        halign: "center",
        valign: "middle",
      },
      columnStyles: { 0: { halign: "center", valign: "middle" } },
      willDrawCell: (hookData) => {
        const { section, column } = hookData;
        if (section !== "body") return;
        const colIndex = column.index;
        if (colIndex >= 1 && colIndex <= monthKeys.length) {
          hookData.cell.text = [];
        }
      },
      didDrawCell: (hookData) => {
        const { section, column, cell } = hookData;
        if (section !== "body") return;

        const colIndex = column.index;
        if (colIndex < 1 || colIndex > monthKeys.length) return;

        const score = cell.raw;
        const band = bandFor(score);
        const colors = BAND_COLORS[band];

        const centerX = cell.x + cell.width / 2;
        const centerY = cell.y + cell.height / 2;
        const radius = Math.min(cell.width, cell.height) / 2 - 1.2;

        doc.setFillColor(...colors.pdf);
        doc.circle(centerX, centerY, radius, "F");

        doc.setFontSize(8);
        doc.setFont(undefined, "bold");
        doc.setTextColor(
          band === "none" ? 71 : 255,
          band === "none" ? 85 : 255,
          band === "none" ? 105 : 255,
        );

        const label =
          score === null || score === undefined ? "—" : String(score);
        doc.text(label, centerX, centerY, {
          align: "center",
          baseline: "middle",
        });
      },
    });

    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i += 1) {
      doc.setPage(i);
      doc.setDrawColor(226, 232, 240);
      doc.line(marginX, pageHeight - 12, pageWidth - marginX, pageHeight - 12);

      doc.setFontSize(7);
      doc.setFont(undefined, "normal");
      doc.setTextColor("#94a3b8");
      doc.text(
        "Generated from Pereco  •  Powered By Perennial Code",
        pageWidth / 2,
        pageHeight - 7,
        { align: "center" },
      );
    }

    doc.save(
      `Consolidated-Growth-Analysis-${selectedMonth.format("MMM-YYYY")}.pdf`,
    );
  };
  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <nav aria-label="breadcrumb">
                <ol className="breadcrumb">
                  <li className="breadcrumb-item">
                    <Link to="/rollcall/dashboard">
                      <i className="fas fa-home fa-lg" />
                    </Link>
                  </li>
                  <h4 className="breadcrumb-item" aria-current="page">
                    Growth Analysis Report
                  </h4>
                </ol>
              </nav>
            </div>
          </div>
          <ul className="table-top-head">
            <li>
              <OverlayTrigger placement="top" overlay={<Tooltip>PDF</Tooltip>}>
                <Link onClick={handleDownloadPdf}>
                  <ImageWithBasePath src="assets/img/icons/pdf.svg" alt="pdf" />
                </Link>
              </OverlayTrigger>
            </li>
          </ul>
        </div>

        <div className="card">
          <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
            <div className="search-set"></div>
            <div className="d-flex table-dropdown my-xl-auto right-content align-items-center flex-wrap row-gap-3">
              <div className="me-2">
                <DatePicker
                  picker="month"
                  value={selectedMonth}
                  onChange={handleMonthChange}
                  allowClear={false}
                  style={{ width: 160 }}
                />
              </div>
            </div>
          </div>

          <div className="card-body">
            {error && (
              <Alert type="error" message={error} showIcon className="mb-3" />
            )}

            {loading ? (
              <div className="d-flex justify-content-center align-items-center py-5">
                <Spin size="large" />
              </div>
            ) : (
              <>
                <Table
                  columns={columns}
                  dataSource={data}
                  rowKey={(record) => record.BranchId}
                  scroll={{ x: true }}
                  bordered
                  pgno={true}
                />

                <div className="d-flex flex-wrap align-items-center gap-4 pt-3 mt-2 border-top">
                  <span className="text-muted text-uppercase small fw-semibold">
                    Score
                  </span>
                  <LegendDot color={BAND_COLORS.red.dot} label="Below 50%" />
                  <LegendDot color={BAND_COLORS.orange.dot} label="50% – 80%" />
                  <LegendDot color={BAND_COLORS.green.dot} label="Above 80%" />
                  <LegendDot color={BAND_COLORS.none.dot} label="No data" />
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function LegendDot({ color, label }) {
  return (
    <span className="d-inline-flex align-items-center gap-2 text-muted small">
      <span
        className="rounded-circle"
        style={{
          width: 10,
          height: 10,
          backgroundColor: color,
          display: "inline-block",
        }}
      />
      {label}
    </span>
  );
}

LegendDot.propTypes = {
  color: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
};
